import { useState } from 'react';
import { api, type StockItem } from '../../api/client';
import { useEditor } from '../../state/editorStore';

const SRC_LABEL: Record<string, string> = { pexels: 'Pexels', pixabay: 'Pixabay', openverse: 'Openverse' };
type Filter = 'all' | 'video' | 'image';

/**
 * "Add B-roll" dialog: upload from device, or search our integrated copyright-
 * free sources (Pexels / Pixabay / Openverse) for an image or video and add it
 * straight to the timeline. Picking a result ingests it server-side (keys are
 * server-only) and stores it as project B-roll.
 */
export function StudioBrollAddDialog({
  open,
  onClose,
  onUpload,
  onPickStock,
}: {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  onPickStock: (item: StockItem) => Promise<void>;
}) {
  const projectId = useEditor((s) => s.projectId);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<StockItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());

  if (!open) return null;

  const search = async () => {
    if (!q.trim() || !projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.stockSearch(projectId, q.trim(), filter);
      setItems(res.items);
      if (!res.enabled) setError('Stock search is not configured on the server.');
      else if (res.items.length === 0) setError('No results — try different keywords.');
    } catch {
      setError('Search failed. Try again.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const pick = async (item: StockItem) => {
    if (addingId) return;
    setAddingId(item.id);
    try {
      await onPickStock(item);
      setAdded((s) => new Set(s).add(item.id));
    } catch {
      setError('Could not add that clip.');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="bad-backdrop" onClick={onClose}>
      <div className="bad" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Add B-roll">
        <div className="bad-head">
          <strong>Add B-roll</strong>
          <button className="bad-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="bad-controls">
          <button className="bad-upload" onClick={() => { onUpload(); onClose(); }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
            </svg>
            Upload from device
          </button>
          <span className="bad-or">or search free stock</span>
        </div>

        <div className="bad-searchrow">
          <input
            className="bad-input"
            placeholder="Search Pexels, Pixabay, Openverse…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            autoFocus
          />
          <div className="bad-seg">
            {(['all', 'video', 'image'] as const).map((f) => (
              <button key={f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'video' ? 'Video' : 'Image'}
              </button>
            ))}
          </div>
          <button className="bad-go" onClick={search} disabled={!q.trim() || loading}>
            {loading ? '…' : 'Search'}
          </button>
        </div>

        <div className="bad-results">
          {error && <div className="bad-msg">{error}</div>}
          {!error && items === null && <div className="bad-msg">Search for footage or images to add.</div>}
          {items && items.length > 0 && (
            <div className="bad-grid">
              {items.map((it) => (
                <div
                  key={it.id}
                  className={`bad-tile${added.has(it.id) ? ' added' : ''}`}
                  title={it.author ? `${it.author} · ${SRC_LABEL[it.source]}` : SRC_LABEL[it.source]}
                >
                  {/* Preview: images show the thumbnail; videos play muted on hover. */}
                  {it.kind === 'video' ? (
                    <video
                      poster={it.thumbUrl || undefined}
                      src={it.downloadUrl}
                      muted
                      loop
                      playsInline
                      preload="none"
                      onMouseEnter={(e) => void e.currentTarget.play().catch(() => {})}
                      onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                    />
                  ) : it.thumbUrl ? (
                    <img src={it.thumbUrl} alt="" loading="lazy" />
                  ) : (
                    <div className="bad-noimg" />
                  )}
                  <span className="bad-kind">{it.kind === 'video' ? '▶ hover' : '🖼'}</span>
                  <span className="bad-src">{SRC_LABEL[it.source]}</span>
                  <button
                    className="bad-add-btn"
                    onClick={() => pick(it)}
                    disabled={addingId === it.id || added.has(it.id)}
                  >
                    {addingId === it.id ? 'Adding…' : added.has(it.id) ? '✓ Added' : '+ Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
